import { FunctionComponent, memo } from "react";
import AgentInteractionMetrics from "./agent-interaction-metrics";
import AgentInteractionsCountMetric from "./agent-interactions-count-metric";
import AgentSentimentMetric from "./agent-sentiment-metric";
import AgentTagMetric from "./agent-tag-metric";
import AgentWritingIssueMetric from "./agent-writing-issue-metric";
import { GRAMMAR_METRIC_ENABLED, SPELLING_METRIC_ENABLED } from "constants/feature-flags";
import { faAngry, faThumbsUp, faBookOpen, faSpellCheck } from "@fortawesome/pro-duotone-svg-icons";
import { IProps } from "./agent-metrics.types";
import { InteractionMetric, WritingIssueType } from "@edgetier/watchtower-types";
import classNames from "classnames";
import { SINGLE_ROW_COUNT } from "./agent-metrics.constants";
import { useFilterAvailableMetrics } from "hooks-for/metrics/use-filter-available-metrics";
import "./agent-metrics.scss";

/**
 * Top-level agent metrics like CSAT, AHT, sentiment percentages etc.
 * @param props.defaultEndDateTime   Default end date filter when none is in the URL.
 * @param props.defaultStartDateTime Default start date filter when none is in the URL.
 * @param props.interactionMetricsAvailable Interaction available for a client
 * @returns                          Agent metrics.
 */
const AgentMetrics: FunctionComponent<IProps> = ({
    defaultEndDateTime,
    defaultStartDateTime,
    interactionMetricsAvailable,
}) => {
    // Find only the available metrics
    const metricsAvailable = useFilterAvailableMetrics([
        InteractionMetric.FirstResponseTime,
        InteractionMetric.QueueTime,
    ]);

    /**
     * Find the number of available metrics which will be displayed
     * "3" is the metrics visible all the time: interactions count, frustration and praise
     * some metrics aren't displayed and they should be removed from counting
     * any feature flags enabled related to metrics and not already considered in computed the interactionMetricsAvailable
     */
    const totalMetricsAvailable =
        3 +
        Object.keys(metricsAvailable).length +
        [GRAMMAR_METRIC_ENABLED, SPELLING_METRIC_ENABLED].filter(Boolean).length;

    return (
        <div
            className={classNames("agent-metrics", {
                "agent-metrics--is-single-row": totalMetricsAvailable <= SINGLE_ROW_COUNT,
                "agent-metrics--is-many-rows": totalMetricsAvailable > SINGLE_ROW_COUNT,
            })}
        >
            <AgentInteractionsCountMetric
                defaultEndDateTime={defaultEndDateTime}
                defaultStartDateTime={defaultStartDateTime}
            />

            <AgentInteractionMetrics
                defaultEndDateTime={defaultEndDateTime}
                defaultStartDateTime={defaultStartDateTime}
                interactionMetricsAvailable={interactionMetricsAvailable}
            />

            <AgentSentimentMetric
                defaultEndDateTime={defaultEndDateTime}
                defaultStartDateTime={defaultStartDateTime}
                filterName="hasFrustration"
                icon={faAngry}
                title="Frustration"
                tooltipContent="Percentage of interactions where customers exhibited frustration in one or more messages"
            />

            <AgentSentimentMetric
                defaultEndDateTime={defaultEndDateTime}
                defaultStartDateTime={defaultStartDateTime}
                filterName="hasPraise"
                icon={faThumbsUp}
                title="Praise"
                tooltipContent="Percentage of interactions where customers exhibited praise in one or more messages"
            />
            {/* Writing issue components are displayed with a feature-flag */}
            {SPELLING_METRIC_ENABLED && (
                <AgentWritingIssueMetric
                    defaultEndDateTime={defaultEndDateTime}
                    defaultStartDateTime={defaultStartDateTime}
                    icon={faSpellCheck}
                    title="Spelling"
                    tooltipContent="The percentage of interactions with spelling mistakes"
                    writingIssueType={WritingIssueType.Spelling}
                />
            )}
            {GRAMMAR_METRIC_ENABLED && (
                <AgentWritingIssueMetric
                    defaultEndDateTime={defaultEndDateTime}
                    defaultStartDateTime={defaultStartDateTime}
                    icon={faBookOpen}
                    title="Grammar"
                    tooltipContent="The percentage of interactions with grammar errors"
                    writingIssueType={WritingIssueType.Grammar}
                />
            )}

            <AgentTagMetric defaultEndDateTime={defaultEndDateTime} defaultStartDateTime={defaultStartDateTime} />
        </div>
    );
};

export default memo(AgentMetrics);
